import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const CANONICAL_BASE = "https://infinitymg.so";

// Idempotent head writer — Helmet handles most cases, but the visual-edits
// dev babel plugin instruments JSX with `x-*` attributes that occasionally
// prevent react-helmet-async from migrating <meta> / <link> children into
// <head>. This effect guarantees the canonical, description and OG/Twitter
// tags ALWAYS land in <head> regardless of Helmet's reconciliation timing.
function syncHeadTag(selector, factory) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = factory();
    if (el) document.head.appendChild(el);
    return;
  }
  factory(el); // mutate in place
}

function setMeta(name, content, kind = "name") {
  syncHeadTag(`meta[${kind}="${name}"]`, (existing) => {
    if (existing) {
      existing.setAttribute("content", content);
      return existing;
    }
    const el = document.createElement("meta");
    el.setAttribute(kind, name);
    el.setAttribute("content", content);
    return el;
  });
}

function setLink(rel, href) {
  // Use a strong selector — there can be multiple <link rel="icon"> etc.
  const sel = `link[rel="${rel}"]:not([type]):not([sizes])`;
  syncHeadTag(sel, (existing) => {
    if (existing) {
      existing.setAttribute("href", href);
      return existing;
    }
    const el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("href", href);
    return el;
  });
}

export default function SEO({
  title,
  description,
  path,
  image = "/api/media/og-image.png",
  jsonLd,
}) {
  const location = useLocation();
  const pathname = path !== undefined ? path : location.pathname;
  const url = `${CANONICAL_BASE}${pathname || "/"}`;
  const og = image?.startsWith("http")
    ? image
    : `${process.env.REACT_APP_BACKEND_URL}${image}`;

  // Belt-and-braces: write tags directly to <head> via effect.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (title) document.title = title;
    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description);
    }
    setLink("canonical", url);
    setMeta("og:url", url, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", "Infinity Medical Group", "property");
    if (title) {
      setMeta("og:title", title, "property");
      setMeta("twitter:title", title);
    }
    setMeta("og:image", og, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", og);

    // JSON-LD injected via a stable id so we can replace on route change.
    const ldId = "img-jsonld";
    let ld = document.getElementById(ldId);
    if (jsonLd) {
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = ldId;
        document.head.appendChild(ld);
      }
      ld.text = JSON.stringify(jsonLd);
    } else if (ld) {
      ld.remove();
    }
  }, [title, description, url, og, jsonLd]);

  // Keep Helmet for the title (Helmet has a special path for <title> that
  // always works) and so SSR/static-rendering paths still see something.
  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Infinity Medical Group" />
      <meta property="og:url" content={url} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={og} />
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={og} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
